import React, { useCallback, useEffect, useRef, useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import TypedConfirmationModal from "../../Components/TypedConfirmationModal";
import LoadingLogo from "../../Components/LoadingLogo";
import IconEmptyView from "../../Components/IconEmptyView";
import { NutritionEmptyState } from "../shared/NutritionEmptyState";
import { NutritionPlanEditorView } from "../plans/editor/NutritionPlanEditorView";
import { createClientNutritionPlanSeed } from "../shared/nutritionPlanSeed";
import {
  CLIENT_NUTRITION_STATUS_ACTIVE,
  CLIENT_NUTRITION_STATUS_ARCHIVED,
} from "../../Firestore/nutrition/nutritionPaths";
import { useToast } from "../../contexts/ToastContext";
import ClientNutritionPlansBrowser from "../plans/clientBrowser/ClientNutritionPlansBrowser";
import ClientNutritionPlanCopyToAthleteModal from "../plans/clientBrowser/ClientNutritionPlanCopyToAthleteModal";
import ClientNutritionPlanExportModal from "../plans/clientBrowser/ClientNutritionPlanExportModal";
import ClientNutritionPlanImportModal from "../plans/clientBrowser/ClientNutritionPlanImportModal";
import {
  copyClientNutritionPlanToClient,
  publishNutritionPlanToClient,
} from "../plans/nutritionPlanAssignments";
import {
  getNutritionPlanActionErrorDescription,
  resolveNutritionPlanActionSnapshot,
} from "../plans/nutritionPlanActionSnapshot";
import { resolveClientAthleteDisplayName } from "../plans/clientBrowser/clientNutritionCopyToAthleteUtils";
import { filterNutritionAssignmentEligibleClients } from "../../invitations/invitedClientUiGuards";
import {
  getNutritionPlanDeleteExpectedValue,
  UNNAMED_NUTRITION_PLAN_DELETE_CONFIRMATION,
  matchesNutritionPlanDeleteConfirmation,
} from "../plans/nutritionPlanDeleteUtils";
import {
  getNutritionPlanDisplayName,
  getNutritionPlanDisplayNameFromSources,
} from "../plans/nutritionPlanNameUtils";
import {
  downloadNutritionPlanPdf,
  downloadNutritionPlansPdf,
} from "../plans/nutritionPlanPdfExport";
import { getNutritionConflictSummary } from "../shared/nutritionConflictWarnings";
import { COACH_NUTRITION_BOOTSTRAP_RESOURCES } from "../coachNutritionBootstrap";
import { shouldFetchInitialClientNutritionPlans } from "./clientNutritionScreenFetch";
import { duplicateClientNutritionPlanFromScreen } from "./clientNutritionScreenDuplicate";
import { resolveOpenedClientNutritionPlan } from "./clientNutritionScreenSelection";
import { resolveOpenedClientNutritionPlanPersistedSource } from "./clientNutritionScreenSelection";
import { shouldKeepOpenedClientNutritionPlanSelection } from "./clientNutritionScreenSelection";
import {
  buildActivePlansPdfExportPlanSources,
  resolveActivePlansForPdfExport,
} from "./clientNutritionScreenPdfExport";
import { NutritionOfflineBlockedView } from "../shared/NutritionOfflineBlockedView";
import {
  navigateClientNutritionOfflineToDashboard,
  shouldBlockClientNutritionOffline,
} from "../shared/nutritionOfflineNavigation";
import { useLatchedEditorPersistedState } from "../shared/useLatchedEditorPersistedState";
import Colors from "../../../Resources/Color";

function resolveClientBrowserPlans(clientPlanStore) {
  if (!clientPlanStore) {
    return [];
  }

  // Read the minimum browser projection needed for this screen so flush-only
  // metadata churn in unrelated store views does not widen the observer surface.
  const propertyCandidates = [
    "listPlans",
    "planListItems",
    "browserPlans",
    "clientBrowserPlans",
    "sortedListPlans",
    "sortedPlanListItems",
    "sortedBrowserPlans",
    "sortedClientBrowserPlans",
    "sortedPlans",
  ];

  for (const propertyName of propertyCandidates) {
    const value = clientPlanStore[propertyName];
    if (Array.isArray(value)) {
      return value;
    }
  }

  const callableCandidates = [
    clientPlanStore.getPlanListItems,
    clientPlanStore.getClientBrowserPlans,
    clientPlanStore.getSortedClientBrowserPlans,
    clientPlanStore.getBrowserPlans,
    clientPlanStore.getPlanBrowserItems,
  ];

  const resolvedList = callableCandidates.find((candidate) => typeof candidate === "function");
  if (!resolvedList) {
    return [];
  }

  const result = resolvedList.call(clientPlanStore);
  return Array.isArray(result) ? result : [];
}

export const ClientNutritionScreen = observer(function ClientNutritionScreen({ client }) {
  const { i18n, t } = useTranslation();
  const { showToast } = useToast();
  const language = i18n?.resolvedLanguage || i18n?.language || "en";
  const clientPlanStore = client?.clientNutritionPlanStore ?? null;
  const isOfflineBlocked = shouldBlockClientNutritionOffline(client);
  const coachNutritionStore = client?.coach?.nutritionStore ?? null;
  const editingPlanId =
    String(client?.nutritionEditingPlanId ?? "").trim().toLowerCase() || null;
  const [importModalState, setImportModalState] = useState({
    isVisible: false,
    sourceFolderKey: null,
  });
  const [exportModalPlan, setExportModalPlan] = useState(null);
  const [isExportingToLibrary, setIsExportingToLibrary] = useState(false);
  const [copyModalState, setCopyModalState] = useState({
    isVisible: false,
    sourcePlan: null,
  });
  const [deleteModalState, setDeleteModalState] = useState({
    isVisible: false,
    plan: null,
    typedValue: "",
  });
  const [planConflictSummariesById, setPlanConflictSummariesById] = useState({});
  const loadingPlanConflictIdsRef = useRef(new Set());
  const planConflictMealsByIdRef = useRef(new Map());

  useEffect(() => {
    if (!shouldFetchInitialClientNutritionPlans(clientPlanStore)) {
      return;
    }

    clientPlanStore.fetchPlansFromCloud().catch(() => {});
  }, [clientPlanStore]);

  useEffect(() => {
    if (
      importModalState.isVisible !== true ||
      !coachNutritionStore ||
      coachNutritionStore.hasFetchedPlans ||
      coachNutritionStore.isFetchingPlans
    ) {
      return;
    }

    client?.coach?.ensureNutritionResourcesFromCloud?.(
      COACH_NUTRITION_BOOTSTRAP_RESOURCES.clientImportModal,
    )?.catch?.(() => {});
  }, [
    client?.coach,
    coachNutritionStore,
    importModalState.isVisible,
  ]);

  const plans = resolveClientBrowserPlans(clientPlanStore);
  const clientDietaryPreference =
    client?.clientSettings?.dietary_preference ?? client?.dietaryPreference ?? "none";
  const clientAllergenCodes = Array.isArray(client?.clientSettings?.allergen_codes)
    ? client.clientSettings.allergen_codes
    : Array.isArray(client?.allergenCodes)
      ? client.allergenCodes
      : [];
  const clientAllergenCodesKey = clientAllergenCodes.join(",");
  const eligibleNutritionTargetClients = filterNutritionAssignmentEligibleClients(
    client?.coach?.clients ?? client?.coach?.activeClients ?? [],
  );
  const athleteName = resolveClientAthleteDisplayName(client);

  useEffect(() => {
    const activePlanIds = new Set(
      plans
        .map((plan) => String(plan?.id ?? "").trim().toLowerCase())
        .filter(Boolean),
    );

    setPlanConflictSummariesById((previousMap) => {
      const nextMap = { ...previousMap };
      let hasChanges = false;

      Object.keys(nextMap).forEach((planId) => {
        if (!activePlanIds.has(planId)) {
          delete nextMap[planId];
          planConflictMealsByIdRef.current.delete(planId);
          hasChanges = true;
        }
      });

      return hasChanges ? nextMap : previousMap;
    });
  }, [plans]);

  useEffect(() => {
    if (!clientPlanStore || !client?.id || plans.length === 0) {
      return;
    }

    let isActive = true;

    const setPlanSummary = (planId, summary) => {
      if (!planId) {
        return;
      }

      setPlanConflictSummariesById((previousMap) => {
        const currentSummary = previousMap[planId];
        const nextSummary = summary ?? { hasConflicts: false, reasons: [] };
        const reasonsMatch =
          Array.isArray(currentSummary?.reasons) &&
          Array.isArray(nextSummary?.reasons) &&
          currentSummary.reasons.length === nextSummary.reasons.length &&
          currentSummary.reasons.every((reason, index) => reason === nextSummary.reasons[index]);

        if (
          currentSummary?.hasConflicts === nextSummary.hasConflicts &&
          reasonsMatch
        ) {
          return previousMap;
        }

        return {
          ...previousMap,
          [planId]: nextSummary,
        };
      });
    };

    plans.forEach((plan) => {
      const planId = String(plan?.id ?? "").trim().toLowerCase();
      if (!planId) {
        return;
      }

      if (
        plan?.planEntity?.mealsHydrated === true &&
        typeof plan?.planEntity?.getDraftSnapshot === "function"
      ) {
        planConflictMealsByIdRef.current.delete(planId);
        setPlanSummary(
          planId,
          getNutritionConflictSummary({
            client,
            source: plan.planEntity.getDraftSnapshot(),
          }),
        );
        return;
      }

      const cachedMealRecords = planConflictMealsByIdRef.current.get(planId);
      if (Array.isArray(cachedMealRecords)) {
        setPlanSummary(
          planId,
          getNutritionConflictSummary({
            client,
            source: {
              ...(plan?.rootRecord ?? {}),
              ...plan,
              meals: cachedMealRecords,
            },
          }),
        );
        return;
      }

      if (loadingPlanConflictIdsRef.current.has(planId)) {
        return;
      }

      loadingPlanConflictIdsRef.current.add(planId);
      clientPlanStore
        .loadPlanMealRecords(planId)
        .then((mealRecords) => {
          if (!isActive) {
            return;
          }

          planConflictMealsByIdRef.current.set(planId, mealRecords);
          setPlanSummary(
            planId,
            getNutritionConflictSummary({
              client,
              source: {
                ...(plan?.rootRecord ?? {}),
                ...plan,
                meals: mealRecords,
              },
            }),
          );
        })
        .catch(() => {
          if (!isActive) {
            return;
          }

          setPlanSummary(planId, { hasConflicts: false, reasons: [] });
        })
        .finally(() => {
          loadingPlanConflictIdsRef.current.delete(planId);
        });
    });

    return () => {
      isActive = false;
    };
  }, [
    client,
    client?.id,
    clientAllergenCodesKey,
    clientDietaryPreference,
    clientPlanStore,
    plans,
  ]);

  const handleCreatePlan = useCallback(
    (status = CLIENT_NUTRITION_STATUS_ACTIVE) => {
      const isArchived = status === CLIENT_NUTRITION_STATUS_ARCHIVED;
      const createdAt = new Date();
      const draftPlan = clientPlanStore?.createDraftPlan?.({
        ...createClientNutritionPlanSeed(),
        createdAt,
        status,
        ...(isArchived ? { deactivated_at: createdAt } : { activated_at: createdAt }),
      }, { isLocalNewPlan: true });

      if (draftPlan?.id) {
        client?.setProperty?.("nutritionEditingPlanId", draftPlan.id);
      }
    },
    [client, clientPlanStore, language],
  );

  const handleOpenPlan = useCallback(
    (plan) => {
      if (!plan?.id) {
        return;
      }

      client?.setProperty?.("nutritionEditingPlanId", plan.id);
    },
    [client],
  );

  const handleMovePlan = useCallback(
    async (plan, targetStatus) => {
      if (!plan?.id || !clientPlanStore) {
        return;
      }

      if (targetStatus === CLIENT_NUTRITION_STATUS_ARCHIVED) {
        await clientPlanStore.archivePlan(plan.id);
        return;
      }

      await clientPlanStore.restorePlan(plan.id);
    },
    [clientPlanStore],
  );

  const handleOpenImportModal = useCallback((sourceFolderKey) => {
    setImportModalState({
      isVisible: true,
      sourceFolderKey,
    });
  }, []);

  const resolvePlanPdfSource = useCallback(
    async (plan) => {
      const normalizedPlanId = String(plan?.id ?? "").trim().toLowerCase();
      if (!normalizedPlanId || !clientPlanStore) {
        throw new Error("Missing nutrition plan source");
      }

      const sourcePlanEntity =
        plan?.planEntity ??
        clientPlanStore?.planEntitiesById?.get?.(normalizedPlanId) ??
        null;
      const sourceRootRecord =
        plan?.rootRecord ??
        clientPlanStore?.planRootRecordsById?.get?.(normalizedPlanId) ??
        null;

      const { planRecord } = await resolveNutritionPlanActionSnapshot({
        planId: normalizedPlanId,
        sourceEntity: sourcePlanEntity,
        sourceRootRecord,
        fallbackPlan: plan,
        loadMealRecords: (resolvedPlanId, options = {}) =>
          clientPlanStore.loadPlanMealRecords(resolvedPlanId, options),
        language,
      });

      return {
        planEntity: null,
        rootRecord: sourceRootRecord,
        draft: planRecord,
      };
    },
    [clientPlanStore, language],
  );

  const handleExportActivePlansPdf = useCallback(async (orderedActivePlans) => {
    const activePlans = resolveActivePlansForPdfExport({
      plans,
      orderedActivePlans,
    });

    if (activePlans.length === 0 || !clientPlanStore) {
      showToast(t("nutritionPlanPdfNoActivePlansTitle-string", "No active plans"), {
        type: "info",
        title: t("nutritionPlanPdfNoActivePlansTitle-string", "No active plans"),
        description: t(
          "nutritionPlanPdfNoActivePlansDescription-string",
          "There are no active nutrition plans to export.",
        ),
      });
      return;
    }

    try {
      const settledSources = await Promise.allSettled(
        activePlans.map(async (plan) => await resolvePlanPdfSource(plan)),
      );
      const planSources = buildActivePlansPdfExportPlanSources({
        activePlans,
        settledSources,
        language,
      });

      const { fileName: downloadedFileName } = await downloadNutritionPlansPdf({
        planSources,
        coach: client?.coach ?? null,
        athleteName,
        language,
      });

      showToast(t("nutritionPlanPdfExportedTitle-string", "PDF exported"), {
        type: "info",
        title: t("nutritionPlanPdfExportedTitle-string", "PDF exported"),
        description: t(
          "nutritionPlanPdfExportedDescription-string",
          'Downloaded "{{name}}".',
          { name: downloadedFileName },
        ),
      });
    } catch (error) {
      console.error("Failed to export active nutrition plans PDF:", error);
      showToast(t("nutritionPlanPdfExportErrorTitle-string", "Could not export PDF"), {
        type: "error",
        title: t("nutritionPlanPdfExportErrorTitle-string", "Could not export PDF"),
        description: getNutritionPlanActionErrorDescription(
          error,
          t(
            "nutritionPlanPdfExportErrorDescription-string",
            "Try again in a moment.",
          ),
          t,
        ),
      });
    }
  }, [
    athleteName,
    client?.coach,
    clientPlanStore,
    language,
    plans,
    resolvePlanPdfSource,
    showToast,
    t,
  ]);

  const handleExportPlanPdf = useCallback(
    async (plan) => {
      const normalizedPlanId = String(plan?.id ?? "").trim().toLowerCase();
      if (!normalizedPlanId || !clientPlanStore) {
        return;
      }

      try {
        const planSource = await resolvePlanPdfSource(plan);

        const downloadedFileName = await downloadNutritionPlanPdf({
          planEntity: planSource.planEntity,
          draft: planSource.draft,
          rootRecord: planSource.rootRecord,
          coach: client?.coach ?? null,
          athleteName,
          language,
        });

        showToast(t("nutritionPlanPdfExportedTitle-string", "PDF exported"), {
          type: "info",
          title: t("nutritionPlanPdfExportedTitle-string", "PDF exported"),
          description: t(
            "nutritionPlanPdfExportedDescription-string",
            'Downloaded "{{name}}".',
            { name: downloadedFileName },
          ),
        });
      } catch (error) {
        console.error("Failed to export nutrition plan PDF from browser row:", error);
        showToast(t("nutritionPlanPdfExportErrorTitle-string", "Could not export PDF"), {
          type: "error",
          title: t("nutritionPlanPdfExportErrorTitle-string", "Could not export PDF"),
          description: getNutritionPlanActionErrorDescription(
            error,
            t(
              "nutritionPlanPdfExportErrorDescription-string",
              "Try again in a moment.",
            ),
            t,
          ),
        });
      }
    },
    [athleteName, client?.coach, clientPlanStore, language, resolvePlanPdfSource, showToast, t],
  );

  const handleOpenExportModal = useCallback((plan) => {
    if (!plan?.id) {
      return;
    }

    setExportModalPlan(plan);
  }, []);

  const handleOpenCopyModal = useCallback((plan) => {
    if (!plan?.id) {
      return;
    }

    setCopyModalState({
      isVisible: true,
      sourcePlan: plan,
    });
  }, []);

  const handleCloseImportModal = useCallback(() => {
    setImportModalState((previousState) => ({
      ...previousState,
      isVisible: false,
    }));
  }, []);

  const handleCloseExportModal = useCallback(() => {
    if (isExportingToLibrary) {
      return;
    }

    setExportModalPlan(null);
  }, [isExportingToLibrary]);

  const handleCloseCopyModal = useCallback(() => {
    setCopyModalState((previousState) => ({
      ...previousState,
      isVisible: false,
    }));
  }, []);

  const handleOpenDeleteModal = useCallback((plan) => {
    if (!plan?.id) {
      return;
    }

    setDeleteModalState({
      isVisible: true,
      plan,
      typedValue: "",
    });
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalState({
      isVisible: false,
      plan: null,
      typedValue: "",
    });
  }, []);

  const handleImportPlan = useCallback(
    async (plan, sourceFolderKey) => {
      if (!client?.id || !client?.coach || !plan?.id) {
        throw new Error("Missing client or nutrition plan for import");
      }

      const targetStatus =
        String(sourceFolderKey ?? "").trim() === CLIENT_NUTRITION_STATUS_ARCHIVED
          ? CLIENT_NUTRITION_STATUS_ARCHIVED
          : CLIENT_NUTRITION_STATUS_ACTIVE;

      try {
        await publishNutritionPlanToClient({
          coach: client.coach,
          client,
          plan,
          status: targetStatus,
        });

        showToast("clientNutritionPlanImportSuccessTitle-string", {
          type: "info",
          title: "clientNutritionPlanImportSuccessTitle-string",
          description:
            targetStatus === CLIENT_NUTRITION_STATUS_ARCHIVED
              ? "clientNutritionPlanImportArchivedSuccessDescription-string"
              : "clientNutritionPlanImportActiveSuccessDescription-string",
        });
      } catch (error) {
        console.error("Failed to import nutrition plan from library:", error);
        showToast("clientNutritionPlanImportErrorTitle-string", {
          type: "error",
          title: "clientNutritionPlanImportErrorTitle-string",
          description: "nutritionCommonTryAgainLater-string",
        });
        throw error;
      }
    },
    [client, showToast],
  );

  const coachLibraryPlans = Array.isArray(coachNutritionStore?.plans)
    ? coachNutritionStore.plans
    : Array.isArray(coachNutritionStore?.sortedPlans)
      ? coachNutritionStore.sortedPlans
      : [];
  const exportInitialName = getNutritionPlanDisplayNameFromSources(
    [
      exportModalPlan?.planEntity?.getDraftSnapshot?.(),
      exportModalPlan?.planEntity,
      exportModalPlan?.rootRecord,
      exportModalPlan,
    ],
    language,
  );
  const copySourcePlanName =
    getNutritionPlanDisplayName(
      copyModalState.sourcePlan?.planEntity ??
        copyModalState.sourcePlan?.rootRecord ??
        copyModalState.sourcePlan?.planEntity?.getDraftSnapshot?.() ??
        copyModalState.sourcePlan,
      language,
    );
  const deletePlanExpectedValue =
    getNutritionPlanDeleteExpectedValue(
      deleteModalState.plan?.planEntity,
      deleteModalState.plan?.rootRecord,
      deleteModalState.plan?.planEntity?.getDraftSnapshot?.(),
      deleteModalState.plan,
    );
  const deletePlanName = getNutritionPlanDisplayName(
    deleteModalState.plan?.planEntity ??
      deleteModalState.plan?.rootRecord ??
      deleteModalState.plan?.planEntity?.getDraftSnapshot?.() ??
      deleteModalState.plan,
    language,
  );
  const isDeletePlanConfirmEnabled = matchesNutritionPlanDeleteConfirmation({
    input: deleteModalState.typedValue,
    expectedValue: deletePlanExpectedValue,
  });

  const handleCopyPlanToAthlete = useCallback(
    async (targetClient) => {
      if (!clientPlanStore || !copyModalState.sourcePlan?.id || !targetClient?.id) {
        throw new Error("Missing source or target client for nutrition plan copy");
      }

      const targetAthleteName =
        resolveClientAthleteDisplayName(targetClient) ||
        t("nutritionAthleteFallback-string", "athlete");

      try {
        await copyClientNutritionPlanToClient({
          sourcePlan: copyModalState.sourcePlan,
          sourceClientPlanStore: clientPlanStore,
          targetClient,
        });

        showToast("clientNutritionPlanCopySuccessTitle-string", {
          type: "info",
          title: "clientNutritionPlanCopySuccessTitle-string",
          description: "clientNutritionPlanCopySuccessDescription-string",
          variables: {
            planName: copySourcePlanName,
            athleteName: targetAthleteName,
          },
        });
      } catch (error) {
        console.error("Failed to copy nutrition plan to another athlete:", error);
        showToast("clientNutritionPlanCopyErrorTitle-string", {
          type: "error",
          title: "clientNutritionPlanCopyErrorTitle-string",
          description: getNutritionPlanActionErrorDescription(
            error,
            t("nutritionCommonTryAgainLater-string", "Try again in a moment."),
            t,
          ),
        });
        throw error;
      }
    },
    [clientPlanStore, copyModalState.sourcePlan, copySourcePlanName, showToast, t],
  );

  const handleExportPlanToLibrary = useCallback(
    async (exportName) => {
      if (!exportModalPlan?.id || !clientPlanStore || !coachNutritionStore) {
        return;
      }

      setIsExportingToLibrary(true);

      try {
        await coachNutritionStore.exportClientPlanToLibrary({
          clientPlan: exportModalPlan,
          clientPlanStore,
          exportName,
        });

        showToast("clientNutritionPlanExportSuccessTitle-string", {
          type: "info",
          title: "clientNutritionPlanExportSuccessTitle-string",
          description: "clientNutritionPlanExportSuccessDescription-string",
          variables: {
            name: exportName,
          },
        });
        setExportModalPlan(null);
      } catch (error) {
        console.error("Failed to export nutrition plan to library:", error);
        showToast("clientNutritionPlanExportErrorTitle-string", {
          type: "error",
          title: "clientNutritionPlanExportErrorTitle-string",
          description: getNutritionPlanActionErrorDescription(
            error,
            t("nutritionCommonTryAgainLater-string", "Try again in a moment."),
            t,
          ),
        });
      } finally {
        setIsExportingToLibrary(false);
      }
    },
    [clientPlanStore, coachNutritionStore, exportModalPlan, showToast, t],
  );

  const executeDuplicatePlan = useCallback(
    async (plan, { shouldOpenDuplicate = false } = {}) => {
      if (!clientPlanStore || !client?.id || !plan?.id) {
        return;
      }

      try {
        return await duplicateClientNutritionPlanFromScreen({
          client,
          language,
          sourceClientPlanStore: clientPlanStore,
          sourcePlan: plan,
          shouldOpenDuplicate,
          showToast,
        });
      } catch (error) {
        console.error("Failed to duplicate nutrition plan:", error);
        showToast("clientNutritionPlanDuplicateErrorTitle-string", {
          type: "error",
          title: "clientNutritionPlanDuplicateErrorTitle-string",
          description: getNutritionPlanActionErrorDescription(
            error,
            t("nutritionCommonTryAgainLater-string", "Try again in a moment."),
            t,
          ),
        });
      }
    },
    [client, clientPlanStore, language, showToast, t],
  );

  const handleDuplicatePlanFromBrowser = useCallback(
    async (plan) => {
      await executeDuplicatePlan(plan, { shouldOpenDuplicate: false });
    },
    [executeDuplicatePlan],
  );

  const handleDuplicatePlanFromEditor = useCallback(
    async (plan) => {
      await executeDuplicatePlan(plan, { shouldOpenDuplicate: true });
    },
    [executeDuplicatePlan],
  );

  const handleConfirmDeletePlan = useCallback(async () => {
    if (!clientPlanStore || !deleteModalState.plan?.id || !isDeletePlanConfirmEnabled) {
      return;
    }

    try {
      await clientPlanStore.deletePlan(deleteModalState.plan.id);
      handleCloseDeleteModal();
      showToast("nutritionPlanDeleteSuccessTitle-string", {
        type: "info",
        title: "nutritionPlanDeleteSuccessTitle-string",
        description: "nutritionPlanDeleteSuccessDescription-string",
        variables: {
          name: deletePlanName,
        },
      });
    } catch (error) {
      console.error("Failed to delete nutrition plan:", error);
      showToast("nutritionPlanDeleteErrorTitle-string", {
        type: "error",
        title: "nutritionPlanDeleteErrorTitle-string",
        description: "nutritionCommonTryAgainLater-string",
      });
    }
  }, [
    clientPlanStore,
    deleteModalState.plan,
    deletePlanName,
    handleCloseDeleteModal,
    isDeletePlanConfirmEnabled,
    showToast,
  ]);

  const selectedOpenedPlan = resolveOpenedClientNutritionPlan({
    editingPlanId,
    clientPlanStore,
    browserPlans: plans,
  });
  const selectedPlanEntity = selectedOpenedPlan?.planEntity ?? null;
  const openedPlanPersistedSource = resolveOpenedClientNutritionPlanPersistedSource(
    selectedOpenedPlan,
  );
  const openedPlanIsPersisted = useLatchedEditorPersistedState(
    editingPlanId,
    openedPlanPersistedSource,
  );
  const selectedPlanRootRecord = selectedOpenedPlan?.rootRecord ?? null;
  const shouldKeepOpenedPlanSelection = shouldKeepOpenedClientNutritionPlanSelection({
    editingPlanId,
    planEntity: selectedPlanEntity,
    clientPlanStore,
  });
  const workspaceTransitionForOpenPlan =
    clientPlanStore?.getWorkspaceTransitionForPlan?.(editingPlanId) ?? null;
  const isWorkspaceTransitioningOpenPlan =
    workspaceTransitionForOpenPlan?.status === "moving";
  const isWorkspaceTransitionErrorOpenPlan =
    workspaceTransitionForOpenPlan?.status === "error";

  useEffect(() => {
    if (editingPlanId && !selectedPlanEntity && !shouldKeepOpenedPlanSelection) {
      client?.setProperty?.("nutritionEditingPlanId", null);
    }
  }, [client, editingPlanId, selectedPlanEntity, shouldKeepOpenedPlanSelection]);

  const handleClosePlanEditor = useCallback(async () => {
    if (editingPlanId) {
      try {
        await clientPlanStore?.finalizePlanEditorClose?.(editingPlanId);
      } catch (error) {
        console.error("Failed to finalize client nutrition plan editor close:", error);
        showToast("nutritionPlanDeleteErrorTitle-string", {
          type: "error",
          title: "nutritionPlanDeleteErrorTitle-string",
          description: "nutritionCommonTryAgainLater-string",
        });
        return false;
      }
    }

    client?.setProperty?.("nutritionEditingPlanId", null);
    return true;
  }, [client, clientPlanStore, editingPlanId, showToast]);
  const handleDeletePlanFromEditor = useCallback(
    async (planSourceLike) => {
      const resolvedPlanSource =
        planSourceLike?.planEntity ||
        planSourceLike?.rootRecord ||
        planSourceLike?.browserPlan
          ? planSourceLike
          : selectedOpenedPlan ?? null;
      const planId =
        resolvedPlanSource?.id ??
        resolvedPlanSource?.planEntity?.id ??
        editingPlanId;
      if (!clientPlanStore || !planId) {
        return;
      }

      const deleteName = getNutritionPlanDisplayNameFromSources(
        [
          resolvedPlanSource?.planEntity?.getDraftSnapshot?.(),
          resolvedPlanSource?.planEntity,
          resolvedPlanSource?.rootRecord,
          resolvedPlanSource?.browserPlan,
          planSourceLike?.getDraftSnapshot?.(),
          planSourceLike,
          selectedPlanEntity?.getDraftSnapshot?.(),
          selectedPlanEntity,
          selectedPlanRootRecord,
        ],
        language,
      );

      try {
        await clientPlanStore.deletePlan(planId);
        const didClose = await handleClosePlanEditor();
        if (!didClose) {
          return;
        }
        showToast("nutritionPlanDeleteSuccessTitle-string", {
          type: "info",
          title: "nutritionPlanDeleteSuccessTitle-string",
          description: "nutritionPlanDeleteSuccessDescription-string",
          variables: {
            name: deleteName,
          },
        });
      } catch (error) {
        console.error("Failed to delete nutrition plan:", error);
        showToast("nutritionPlanDeleteErrorTitle-string", {
          type: "error",
          title: "nutritionPlanDeleteErrorTitle-string",
          description: "nutritionCommonTryAgainLater-string",
        });
        throw error;
      }
    },
    [
      clientPlanStore,
      editingPlanId,
      handleClosePlanEditor,
      language,
      selectedOpenedPlan,
      selectedPlanEntity,
      selectedPlanRootRecord,
      showToast,
    ],
  );

  const handleGoToDashboard = useCallback(() => {
    navigateClientNutritionOfflineToDashboard(client);
  }, [client]);

  if (isOfflineBlocked) {
    return <NutritionOfflineBlockedView onGoToDashboard={handleGoToDashboard} />;
  }

  if (clientPlanStore && isWorkspaceTransitioningOpenPlan) {
    return (
      <View style={styles.transitionContainer}>
        <LoadingLogo container={false} dark={true} width={76} height={76} />
        <Text style={styles.transitionText}>
          {t(
            "nutritionPlanWorkspaceTransitionDescription-string",
            "Moving nutrition plan to athlete workspace...",
          )}
        </Text>
      </View>
    );
  }

  if (clientPlanStore && isWorkspaceTransitionErrorOpenPlan) {
    return (
      <IconEmptyView
        iconLibrary="Ionicons"
        iconName="warning-outline"
        mainText="nutritionPlanWorkspaceTransitionErrorTitle-string"
        subText="nutritionPlanWorkspaceTransitionErrorDescription-string"
        variant="warning"
      />
    );
  }

  if (clientPlanStore && selectedPlanEntity) {
    return (
      <NutritionPlanEditorView
        coach={client?.coach ?? null}
        client={client}
        planStore={clientPlanStore}
        planEntity={selectedPlanEntity}
        planRootRecord={selectedPlanRootRecord}
        isEditing={openedPlanIsPersisted}
        onBack={handleClosePlanEditor}
        onDuplicate={() =>
          handleDuplicatePlanFromEditor(selectedOpenedPlan ?? selectedPlanEntity)
        }
        onDelete={() => handleDeletePlanFromEditor(selectedOpenedPlan ?? selectedPlanEntity)}
      />
    );
  }

  if (clientPlanStore) {
    return (
      <View style={styles.container}>
        <ClientNutritionPlansBrowser
          onDeletePlan={handleOpenDeleteModal}
          onDuplicatePlan={handleDuplicatePlanFromBrowser}
          onExportPlanPdf={handleExportPlanPdf}
          plans={plans}
          onCopyToAthlete={handleOpenCopyModal}
          onCreatePlan={handleCreatePlan}
          onExportActivePlansPdf={handleExportActivePlansPdf}
          onExportToLibrary={handleOpenExportModal}
          onImportFromLibrary={handleOpenImportModal}
          onOpenPlan={handleOpenPlan}
          onMovePlan={handleMovePlan}
          planConflictSummariesById={planConflictSummariesById}
        />
        <ClientNutritionPlanExportModal
          initialName={exportInitialName}
          isSaving={isExportingToLibrary}
          isVisible={Boolean(exportModalPlan)}
          onClose={handleCloseExportModal}
          onConfirm={handleExportPlanToLibrary}
        />
        <ClientNutritionPlanImportModal
          athleteName={athleteName}
          isLoading={coachNutritionStore?.isFetchingPlans === true}
          isVisible={importModalState.isVisible}
          onClose={handleCloseImportModal}
          onSelectPlan={handleImportPlan}
          plans={coachLibraryPlans}
          sourceFolderKey={importModalState.sourceFolderKey}
        />
        <ClientNutritionPlanCopyToAthleteModal
          athletes={eligibleNutritionTargetClients}
          currentClientId={client?.id ?? null}
          isVisible={copyModalState.isVisible}
          onClose={handleCloseCopyModal}
          onSelectAthlete={handleCopyPlanToAthlete}
          sourcePlan={copyModalState.sourcePlan}
        />
        <TypedConfirmationModal
          isVisible={deleteModalState.isVisible}
          onClose={handleCloseDeleteModal}
          title={t(
            "nutritionPlanDeleteConfirmationTitle-string",
            "DELETE NUTRITION PLAN",
          )}
          description={t(
            "nutritionPlanDeleteConfirmationDescription-string",
            "This will permanently delete the nutrition plan. This action cannot be undone.",
          )}
          instruction={t(
            "nutritionPlanDeleteConfirmationInstruction-string",
            'Type "{{value}}" to confirm.',
            {
              value:
                deletePlanExpectedValue === UNNAMED_NUTRITION_PLAN_DELETE_CONFIRMATION
                  ? UNNAMED_NUTRITION_PLAN_DELETE_CONFIRMATION
                  : deletePlanExpectedValue,
            },
          )}
          placeholder={
            deletePlanExpectedValue === UNNAMED_NUTRITION_PLAN_DELETE_CONFIRMATION
              ? UNNAMED_NUTRITION_PLAN_DELETE_CONFIRMATION
              : t(
                  "nutritionPlanDeleteConfirmationPlaceholder-string",
                  "Type nutrition plan name",
                )
          }
          value={deleteModalState.typedValue}
          onChangeText={(typedValue) =>
            setDeleteModalState((previousState) => ({
              ...previousState,
              typedValue,
            }))
          }
          onConfirm={handleConfirmDeletePlan}
          isConfirmEnabled={isDeletePlanConfirmEnabled}
          confirmLabel={t("delete-string", "Delete")}
          cancelLabel={t("cancel-string", "Cancel")}
        />
      </View>
    );
  }

  return (
    <NutritionEmptyState
      eyebrow={t("nutrition-string", "Nutrition")}
      title={
        clientPlanStore
          ? t("clientNutritionEmptyTitle-string", "Client nutrition plans")
          : t(
              "clientNutritionWorkspaceTitle-string",
              "Rebuilt athlete nutrition workspace",
            )
      }
      description={
        clientPlanStore
          ? t(
              "clientNutritionEmptyDescription-string",
              "The client nutrition editor path is ready for the upcoming plan list and selection flow.",
            )
          : t(
              "clientNutritionWorkspaceDescription-string",
              "The rebuilt client-side nutrition experience will live here as the default nutrition workspace.",
            )
      }
    />
  );
});

const styles = StyleSheet.create({
  container: {
    gap: 16,
  },
  transitionContainer: {
    flex: 1,
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  transitionText: {
    marginTop: 18,
    fontSize: 14,
    fontWeight: "500",
    color: Colors.darkgrayColor,
    textAlign: "center",
  },
});
